import { monkeyPatch } from "./monkeyPatch";

/**
 * Erweitert alle Javascript-Strings um einen Satz von Hilfsmethoden (Monkeypatching).
 * Weitere String-Methoden finden sich in lodash.
 */
monkeyPatch(String.prototype, {
  /**
   * Garantiert, dass der String mit part anfängt.
   * @example
   * 'Hallo'.ensureLeft('/') => '/Hallo'
   * '/Hallo'.ensureLeft('/') => '/Hallo'
   * @param {string} part
   * @returns {string}
   */
  ensureLeft(this: string, part: string): string {
    if (this.startsWith(part)) {
      return this;
    } else {
      return part + this;
    }
  },

  /**
   * Garantiert, dass der String mit part endet.
   * @example
   * 'Hallo'.ensureRight('/') => 'Hallo/'
   * 'Hallo/'.ensureRight('/') => 'Hallo/'
   * @param {string} part
   * @returns {string}
   */
  ensureRight(this: string, part: string): string {
    if (this.endsWith(part)) {
      return this;
    } else {
      return this + part;
    }
  },

  /**
   * Testet, ob der String mit part anfängt.
   * @param {string} part
   * @returns {boolean}
   */
  startsWith(this: string, part: string): boolean {
    return this.indexOf(part) === 0;
  },

  /**
   * Testet, ob der String part enthält.
   * @param {string} part
   * @returns {boolean}
   */
  contains(this: string, part: string): boolean {
    return this.indexOf(part) !== -1;
  },

  /**
   * Testet, ob der String mit part endet.
   * @param {string} part
   * @returns {boolean}
   */
  endsWith(this: string, part: string): boolean {
    return this.indexOf(part) === this.length - part.length;
  },

  /**
   * Testet, ob der String leer ist
   * @returns {boolean}
   */
  isEmpty(this: string): boolean {
    return this.length === 0;
  },

  /**
   * Testet, ob der String leer ist oder nur aus Leerzeichen besteht
   * @returns {boolean}
   */
  isBlank(this: string): boolean {
    return this.trim().length === 0;
  },

  /**
   * Gibt den String in umgekehrter Reihenfolge zurück
   * @returns {string}
   */
  reverse(this: string): string {
    return this.split("").reverse().join("");
  },

  /**
   * Schneidet alle Vorkommen von part von links ab.
   * @example
   * 'aaabaaa'.ltrim('a') => 'baaa'
   * @param {string} part
   * @returns {string}
   */
  ltrim(this: string, part: string): string {
    let s = this.toString();
    while (s.startsWith(part)) {
      s = s.substring(part.length);
    }
    return s;
  },

  /**
   * Schneidet alle Vorkommen von part von rechts ab.
   * @example
   * 'aaabaaa'.rtrim('a') => 'aaab'
   * @param {string} part
   * @returns {string}
   */
  rtrim(this: string, part: string): string {
    let s = this.toString();
    while (s.endsWith(part)) {
      s = s.substring(0, s.length - part.length);
    }
    return s;
  },

  /**
   * Gibt den Teilstring vor dem ersten Auftauchen von part zurück
   * @example
   * 'pfad/unterpfad/endpfad'.substringBefore('/') => 'pfad'
   * 'pfad'.substringBefore('/') => 'pfad'
   * @param {string} part
   * @returns {string}
   */
  substringBefore(this: string, part: string): string {
    let i = this.indexOf(part);
    if (i != -1) {
      return this.slice(0, i);
    } else {
      return this.toString();
    }
  },

  /**
   * Gibt den Teilstring vor dem letzten Auftauchen von part zurück
   * @example
   * 'pfad/unterpfad/endpfad'.substringBefore('/') => 'pfad/unterpfad'
   * 'pfad'.substringBefore('/') => 'pfad'
   * @param {string} part
   * @returns {string}
   */
  substringBeforeLast(this: string, part: string): string {
    let i = this.lastIndexOf(part);
    if (i != -1) {
      return this.slice(0, i);
    } else {
      return this.toString();
    }
  },

  /**
   * Gibt den Teilstring nach dem ersten Auftauchen von part zurück
   * @example
   * 'pfad/unterpfad/endpfad'.substringAfter('/') => 'unterpfad/endpfad'
   * 'pfad'.substringBefore('/') => 'pfad'
   * @param {string} part
   * @returns {string}
   */
  substringAfter(this: string, part: string): string {
    let i = this.indexOf(part);
    if (i != -1) {
      return this.slice(i + part.length, this.length);
    } else {
      return this.toString();
    }
  },

  /**
   * Gibt den Teilstring nach dem letzten Auftauchen von part zurück
   * @example
   * 'pfad/unterpfad/endpfad'.substringAfterLast('/') => 'endpfad'
   * 'pfad'.substringBefore('/') => 'pfad'
   * @param {string} part
   * @returns {string}
   */
  substringAfterLast(this: string, part: string): string {
    let i = this.lastIndexOf(part);
    if (i != -1) {
      return this.slice(i + part.length, this.length);
    } else {
      return this.toString();
    }
  },

  /**
   * Gibt den Teilstring zwischen den Teilstrings left und right zurück
   * @example
   * 'a(b)c'.substringBetween('(',')') => 'b'
   * @param {string} part
   * @returns {string}
   */
  substringBetween(this: string, left: string, right: string): string {
    let l = this.indexOf(left);
    let r = this.lastIndexOf(right);
    if (l < r && l > -1) {
      return this.slice(l + 1, r);
    } else {
      return this.toString();
    }
  },
});
